/**
 * Feb 2026 · Full-page Studio Space editor at /app/studio/spaces/:spaceId
 * Tabs: Overview · Analytics · Payments · Details · Media · Availability
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Save, TrendingUp, IndianRupee, Users, Clock3, CreditCard, Mail, Eye, EyeOff, ChevronRight, Grid3x3, FileText } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function StudioSpaceDetail() {
  const { spaceId } = useParams();
  const { activeWorkspaceId } = useAuth();
  const ws = activeWorkspaceId;
  const [space, setSpace] = useState(null);
  const [tab, setTab] = useState("overview");
  useEffect(() => {
    if (!ws || !spaceId) return;
    api.get(`/workspaces/${ws}/studio/spaces`)
      .then((r) => setSpace((r.data.spaces || []).find((s) => s.id === spaceId) || null))
      .catch(() => setSpace(null));
  }, [ws, spaceId]);
  if (!space) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/app/studio" className="text-primary text-xs hover:underline flex items-center gap-1 mb-4">
          <ArrowLeft className="w-3 h-3" /> Back to Studio
        </Link>
        <div className="py-16 text-center text-muted-foreground text-sm italic">Loading space…</div>
      </div>
    );
  }
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="sb-space-detail">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <Link to="/app/studio" className="text-primary text-xs hover:underline flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Studio · Spaces
          </Link>
          <h1 className="text-3xl font-bold mt-1" data-testid="sb-space-detail-name">{space.name}</h1>
          <div className="text-xs text-muted-foreground uppercase tracking-widest mt-0.5">
            Studio space · {space.slot_mode || "hour + day"} · ₹{Number(space.hourly_rate || 0).toFixed(0)}/hr
          </div>
        </div>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none">
          <TabsTrigger value="overview" className="rounded-none" data-testid="sb-space-tab-overview"><TrendingUp className="w-3.5 h-3.5 mr-1" /> Overview</TabsTrigger>
          <TabsTrigger value="analytics" className="rounded-none" data-testid="sb-space-tab-analytics"><TrendingUp className="w-3.5 h-3.5 mr-1" /> Analytics</TabsTrigger>
          <TabsTrigger value="payments" className="rounded-none" data-testid="sb-space-tab-payments"><CreditCard className="w-3.5 h-3.5 mr-1" /> Payments &amp; Mail</TabsTrigger>
          <TabsTrigger value="templates" className="rounded-none" data-testid="sb-space-tab-templates"><FileText className="w-3.5 h-3.5 mr-1" /> Email templates</TabsTrigger>
          <TabsTrigger value="details" className="rounded-none" data-testid="sb-space-tab-details">Details</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="pt-4"><OverviewTab ws={ws} spaceId={spaceId} /></TabsContent>
        <TabsContent value="analytics" className="pt-4"><AnalyticsTab ws={ws} spaceId={spaceId} /></TabsContent>
        <TabsContent value="payments" className="pt-4"><PaymentsTab ws={ws} space={space} /></TabsContent>
        <TabsContent value="templates" className="pt-4"><EmailTemplatesTab ws={ws} space={space} /></TabsContent>
        <TabsContent value="details" className="pt-4">
          <AmenitiesEditor ws={ws} space={space} onSaved={(next) => { /* parent handles reload on nav */ }} />
          <div className="text-xs text-muted-foreground italic p-4 border border-border mt-4">
            For rates, name and media edits use the &ldquo;Studios &amp; Spaces&rdquo; tab on the main <Link className="text-primary underline" to="/app/studio">Studio page</Link>.
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="border border-border p-4 bg-background" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function OverviewTab({ ws, spaceId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/workspaces/${ws}/studio/spaces/${spaceId}/analytics?months=3`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [ws, spaceId]);
  if (!data) return <div className="text-xs text-muted-foreground italic py-8">Loading overview…</div>;
  const tm = data.this_month || {};
  const lm = data.last_month || {};
  const rev = tm.revenue || 0;
  const revPrev = lm.revenue || 0;
  const delta = revPrev ? Math.round(((rev - revPrev) / revPrev) * 100) : (rev ? 100 : 0);
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <KpiCard icon={IndianRupee} label="Revenue · this month" value={`₹${(tm.revenue || 0).toFixed(0)}`} sub={`${delta >= 0 ? "▲" : "▼"} ${Math.abs(delta)}% vs prev`} testid="sb-space-kpi-revenue" />
      <KpiCard icon={Users} label="Bookings · this month" value={tm.bookings || 0} sub={`${data.totals.bookings || 0} in trailing window`} testid="sb-space-kpi-bookings" />
      <KpiCard icon={Clock3} label="Utilization · this month" value={`${tm.utilization_pct || 0}%`} sub={`${tm.hours || 0}h shot`} testid="sb-space-kpi-utilization" />
      <KpiCard icon={TrendingUp} label="Avg booking value" value={`₹${(data.totals.avg_booking_value || 0).toFixed(0)}`} sub={`${data.totals.repeat_customers || 0} repeat customers`} testid="sb-space-kpi-avg" />
    </div>
  );
}

function AnalyticsTab({ ws, spaceId }) {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(12);
  useEffect(() => {
    api.get(`/workspaces/${ws}/studio/spaces/${spaceId}/analytics?months=${months}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [ws, spaceId, months]);
  if (!data) return <div className="text-xs text-muted-foreground italic py-8">Loading analytics…</div>;
  const trend = data.trend || [];
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold uppercase tracking-widest">Trend</div>
        <select value={months} onChange={(e) => setMonths(Number(e.target.value))}
                  className="h-8 border border-border bg-background text-xs rounded-none px-2"
                  data-testid="sb-space-analytics-range">
          <option value={3}>Last 3 months</option>
          <option value={6}>Last 6 months</option>
          <option value={12}>Last 12 months</option>
          <option value={24}>Last 24 months</option>
        </select>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="border border-border p-3" data-testid="sb-space-chart-revenue">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Revenue by month</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="2 4" opacity={0.4} />
              <XAxis dataKey="month" fontSize={10} />
              <YAxis fontSize={10} width={50} />
              <Tooltip formatter={(v) => `₹${Number(v).toLocaleString()}`} />
              <Line type="monotone" dataKey="revenue" stroke="#EA5934" strokeWidth={2} dot />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-border p-3" data-testid="sb-space-chart-bookings">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Bookings by month</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trend}>
              <CartesianGrid strokeDasharray="2 4" opacity={0.4} />
              <XAxis dataKey="month" fontSize={10} />
              <YAxis fontSize={10} width={30} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="bookings" fill="#111" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-border p-3" data-testid="sb-space-chart-util">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Utilization % by month</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trend}>
              <CartesianGrid strokeDasharray="2 4" opacity={0.4} />
              <XAxis dataKey="month" fontSize={10} />
              <YAxis fontSize={10} width={30} unit="%" domain={[0, 100]} />
              <Tooltip formatter={(v) => `${v}%`} />
              <Bar dataKey="utilization_pct" fill="#0F766E" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-border p-3 lg:col-span-2" data-testid="sb-space-heatmap">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1">
            <Grid3x3 className="w-3 h-3" /> Utilization heatmap · day × 30-min slot (trailing window)
          </div>
          <UtilizationHeatmap heatmap={data.heatmap} ws={ws} spaceId={spaceId} months={months} />
        </div>
        <div className="border border-border p-3" data-testid="sb-space-top-customers">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Top customers by spend</div>
          {(data.top_customers || []).length === 0
            ? <div className="text-xs italic text-muted-foreground py-6 text-center">No paying customers yet.</div>
            : (
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left px-2 py-1 uppercase tracking-widest text-[10px] text-muted-foreground">Customer</th>
                    <th className="text-right px-2 py-1 uppercase tracking-widest text-[10px] text-muted-foreground">Bookings</th>
                    <th className="text-right px-2 py-1 uppercase tracking-widest text-[10px] text-muted-foreground">Spend</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_customers.map((c, i) => (
                    <tr key={c.email} className="border-t border-border" data-testid={`sb-space-top-customer-${i}`}>
                      <td className="px-2 py-1.5">{c.name}<div className="text-[10px] text-muted-foreground">{c.email}</div></td>
                      <td className="px-2 py-1.5 text-right font-mono">{c.bookings}</td>
                      <td className="px-2 py-1.5 text-right font-mono">₹{c.spend.toFixed(0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      </div>
    </div>
  );
}

function PaymentsTab({ ws, space }) {
  const [cf, setCf] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ enabled: false, mode: "TEST", client_id: "", client_secret: "", webhook_secret: "" });
  const [showSecret, setShowSecret] = useState(false);
  const [mailProfileId, setMailProfileId] = useState(space?.mail_profile_id || "");
  useEffect(() => {
    api.get(`/workspaces/${ws}/studio/spaces/${space.id}/cashfree`)
      .then((r) => { setCf(r.data); setForm((f) => ({ ...f, enabled: r.data.enabled, mode: r.data.mode })); })
      .catch(() => setCf({ enabled: false, mode: "TEST" }));
    api.get(`/workspaces/${ws}/mail-profiles`).then((r) => setProfiles(r.data?.profiles || r.data || [])).catch(() => setProfiles([]));
  }, [ws, space.id]);
  const saveCashfree = async () => {
    setBusy(true);
    try {
      const body = { enabled: form.enabled, mode: form.mode };
      if (form.client_id) body.client_id = form.client_id;
      if (form.client_secret) body.client_secret = form.client_secret;
      if (form.webhook_secret) body.webhook_secret = form.webhook_secret;
      const { data } = await api.put(`/workspaces/${ws}/studio/spaces/${space.id}/cashfree`, body);
      setCf(data);
      setForm({ ...form, client_id: "", client_secret: "", webhook_secret: "" });
      toast.success("Cashfree overrides saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const saveMail = async () => {
    setBusy(true);
    try {
      await api.patch(`/workspaces/${ws}/studio/spaces/${space.id}`, { mail_profile_id: mailProfileId || null });
      toast.success("Mail profile linked");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-6">
      <div className="border border-border p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <CreditCard className="w-4 h-4 text-primary" />
          <h2 className="text-sm font-bold uppercase tracking-widest">Cashfree keys · this space only</h2>
          {/* Feb 2026 · Mode badge — bright TEST/LIVE indicator so admins never
              accidentally launch a marketing push in sandbox mode. */}
          {cf && (
            <span data-testid="sb-space-cf-mode-badge"
                   className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border font-bold ${
                     !form.enabled ? "bg-neutral-100 text-neutral-500 border-neutral-300"
                     : form.mode === "LIVE" ? "bg-emerald-50 text-emerald-800 border-emerald-400"
                     : "bg-amber-50 text-amber-800 border-amber-400"}`}>
              {!form.enabled ? "Inherit workspace" : form.mode === "LIVE" ? "● LIVE production" : "● TEST sandbox"}
              {cf.enabled !== form.enabled || cf.mode !== form.mode ? " · unsaved" : ""}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-4 max-w-lg">
          Route payments for bookings on <b>{space.name}</b> to a different Cashfree merchant account.
          If left blank, checkout falls back to the workspace-level Cashfree config.
          Secrets are encrypted at rest and never echoed back.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                    data-testid="sb-space-cf-enabled" />
            <span>Use per-space Cashfree keys</span>
          </label>
          <div>
            <Label className="text-xs">Environment</Label>
            <div className="flex border border-border rounded-none overflow-hidden" data-testid="sb-space-cf-mode-toggle">
              {[
                { v: "TEST", label: "Test (sandbox)", hint: "Use for internal testing — cards from Cashfree docs are accepted" },
                { v: "LIVE", label: "Live (production)", hint: "Real payments — funds settle to your linked bank" },
              ].map((opt) => (
                <button key={opt.v} type="button"
                        onClick={() => setForm({ ...form, mode: opt.v })}
                        title={opt.hint}
                        className={`flex-1 py-2 px-2 text-[11px] uppercase tracking-widest font-semibold transition-colors ${
                          form.mode === opt.v
                            ? (opt.v === "LIVE" ? "bg-emerald-600 text-white" : "bg-amber-500 text-white")
                            : "bg-white text-muted-foreground hover:bg-muted"}`}
                        data-testid={`sb-space-cf-mode-${opt.v.toLowerCase()}`}>
                  {opt.label}
                </button>
              ))}
            </div>
            {/* Native select kept for existing tests + accessibility */}
            <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}
                     className="sr-only" data-testid="sb-space-cf-mode" aria-hidden>
              <option value="TEST">TEST</option>
              <option value="LIVE">LIVE</option>
            </select>
            {form.mode === "LIVE" && !cf?.has_client_id && (
              <div className="text-[10px] text-rose-700 mt-1">
                ⚠ Live mode selected but no client ID is set — payments will fail until you paste live keys below.
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Client ID {cf?.has_client_id && <span className="text-emerald-700 ml-1">· configured</span>}</Label>
            <Input value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                    placeholder={cf?.has_client_id ? "•••••••• (leave blank to keep)" : "cf_test_XXXXXX"}
                    className="rounded-none h-9 font-mono text-xs" data-testid="sb-space-cf-client-id" />
          </div>
          <div>
            <Label className="text-xs flex items-center justify-between">
              <span>Client Secret {cf?.has_client_secret && <span className="text-emerald-700 ml-1">· configured</span>}</span>
              <button type="button" className="text-[10px] text-muted-foreground hover:text-foreground"
                        onClick={() => setShowSecret((v) => !v)}>
                {showSecret ? <><EyeOff className="w-3 h-3 inline" /> hide</> : <><Eye className="w-3 h-3 inline" /> show</>}
              </button>
            </Label>
            <Input type={showSecret ? "text" : "password"} value={form.client_secret}
                    onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                    placeholder={cf?.has_client_secret ? "•••••••• (leave blank to keep)" : "•••"}
                    className="rounded-none h-9 font-mono text-xs" data-testid="sb-space-cf-client-secret" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Webhook secret (opt.) {cf?.has_webhook_secret && <span className="text-emerald-700 ml-1">· configured</span>}</Label>
            <Input value={form.webhook_secret} onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })}
                    placeholder={cf?.has_webhook_secret ? "•••••••• (leave blank to keep)" : "(optional)"}
                    className="rounded-none h-9 font-mono text-xs" data-testid="sb-space-cf-webhook-secret" />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button className="rounded-none" onClick={saveCashfree} disabled={busy} data-testid="sb-space-cf-save">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Saving…" : "Save Cashfree"}
          </Button>
        </div>
      </div>
      <div className="border border-border p-5">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="w-4 h-4 text-primary" />
          <h2 className="text-sm font-bold uppercase tracking-widest">Confirmation-email mail profile</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4 max-w-lg">
          Send booking confirmations, reminders and deposit-release emails via a specific mail profile (Zeptomail / SMTP / Resend).
          Leave empty to inherit the workspace-level Studio mail profile.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label className="text-xs">Mail profile</Label>
            <select value={mailProfileId || ""} onChange={(e) => setMailProfileId(e.target.value)}
                     className="w-full h-9 border border-border bg-background text-xs rounded-none px-2"
                     data-testid="sb-space-mail-profile">
              <option value="">— Use workspace default —</option>
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>{p.name} · {p.provider}</option>
              ))}
            </select>
            {profiles.length === 0 && (
              <div className="mt-1 text-[10px] text-muted-foreground">
                No mail profiles yet. <Link className="text-primary underline" to="/app/settings/mail-profiles">Create one</Link> in Settings.
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button className="rounded-none" onClick={saveMail} disabled={busy} data-testid="sb-space-mail-save">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Saving…" : "Save mail profile"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Feb 2026 · Day × 30-min-slot heatmap. Each cell shades from light
// to primary based on how "busy" it is relative to the max cell.
function UtilizationHeatmap({ heatmap, ws, spaceId, months }) {
  const matrix = heatmap?.matrix || [];
  const maxM = Math.max(1, heatmap?.max_minutes || 0);
  const dayLabels = heatmap?.day_labels || ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const hourLabels = Array.from({ length: 24 }, (_, i) => i);
  const [drill, setDrill] = useState(null);   // {dow, bucket, loading, list}
  const openCell = async (dow, bucket) => {
    setDrill({ dow, bucket, loading: true, list: [] });
    try {
      const { data } = await api.get(`/workspaces/${ws}/studio/spaces/${spaceId}/heatmap-cell`,
        { params: { dow, bucket, months: months || 12 } });
      setDrill({ dow, bucket, loading: false, list: data.bookings || [] });
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); setDrill(null); }
  };
  const cellColor = (mins) => {
    if (mins === 0) return "rgb(245,246,247)";
    const alpha = Math.max(0.08, Math.min(1, mins / maxM));
    return `rgba(234, 89, 52, ${alpha})`;
  };
  const timeLabel = (bucket) => {
    const h = Math.floor(bucket / 2), m = bucket % 2 === 0 ? "00" : "30";
    return `${String(h).padStart(2, "0")}:${m}`;
  };
  return (
    <div className="overflow-x-auto">
      <div className="inline-block" data-testid="sb-space-heatmap-grid">
        <div className="flex text-[9px] text-muted-foreground pl-8 mb-0.5">
          {hourLabels.map((h) => (
            <div key={h} style={{ width: 20 }} className="text-center">{h % 3 === 0 ? h : ""}</div>
          ))}
        </div>
        {matrix.map((row, dow) => (
          <div key={dow} className="flex items-center gap-1">
            <div className="w-7 text-[10px] font-semibold text-muted-foreground">{dayLabels[dow]}</div>
            {row.map((mins, bucket) => {
              const hour = Math.floor(bucket / 2);
              const half = bucket % 2 === 0 ? "00" : "30";
              return (
                <div key={bucket}
                     onClick={() => mins > 0 && openCell(dow, bucket)}
                     title={`${dayLabels[dow]} ${String(hour).padStart(2, "0")}:${half} · ${mins} minutes booked${mins > 0 ? " (click for details)" : ""}`}
                     style={{ width: 10, height: 18, background: cellColor(mins),
                                 border: "1px solid rgba(0,0,0,0.04)",
                                 cursor: mins > 0 ? "pointer" : "default" }}
                     data-testid={`sb-space-heatmap-cell-${dow}-${bucket}`} />
              );
            })}
          </div>
        ))}
        <div className="flex items-center gap-2 mt-2 text-[10px] text-muted-foreground pl-8">
          <span>Idle</span>
          <div className="flex">
            {[0.08, 0.25, 0.5, 0.75, 1].map((a, i) => (
              <div key={i} style={{ width: 20, height: 8, background: `rgba(234,89,52,${a})` }} />
            ))}
          </div>
          <span>Peak ({maxM} min)</span>
        </div>
      </div>
      {drill && (
        <Dialog open onOpenChange={(v) => !v && setDrill(null)}>
          <DialogContent className="rounded-none max-w-lg max-h-[80vh] overflow-y-auto"
                            data-testid="sb-space-heatmap-drill">
            <DialogHeader>
              <DialogTitle>{dayLabels[drill.dow]} · {timeLabel(drill.bucket)}</DialogTitle>
              <div className="text-xs text-muted-foreground">Bookings that touched this slot</div>
            </DialogHeader>
            <div className="space-y-1">
              {drill.loading && <div className="text-xs italic text-muted-foreground">Loading…</div>}
              {!drill.loading && drill.list.length === 0 && (
                <div className="text-xs italic text-muted-foreground py-6 text-center">No bookings.</div>
              )}
              {!drill.loading && drill.list.map((b) => (
                <div key={b.id} className="border border-border p-2 text-xs"
                     data-testid={`sb-space-heatmap-drill-row-${b.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-mono">{b.code}</div>
                    <div className="text-[10px] text-muted-foreground">{b.slot_start?.slice(0, 16).replace("T", " ")} → {b.slot_end?.slice(11, 16)}</div>
                  </div>
                  <div>{b.customer_name}</div>
                  <div className="text-[10px] flex gap-2 mt-0.5">
                    <span className="text-muted-foreground">{b.status}</span>
                    {b.is_off_hours && <span className="text-amber-700">off-hours</span>}
                    <span className="ml-auto font-mono">₹{b.total}</span>
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Feb 2026 · Per-space email-template editor. Overrides subject + HTML
// body for confirmation / reminder / cancellation emails so multi-brand
// agencies can send on-brand transactional copy per space. Supports
// {{code}} {{customer_name}} {{customer_email}} {{slot_start}}
// {{slot_end}} {{total}} {{space_name}} placeholders.
function EmailTemplatesTab({ ws, space }) {
  const kinds = [
    { k: "confirmed", label: "Confirmation email", hint: "Sent right after payment succeeds." },
    { k: "reminder",  label: "Reminder email",     hint: "Sent 24h and 2h before the shoot." },
    { k: "cancelled", label: "Cancellation email", hint: "Sent when a booking is cancelled." },
  ];
  const [tpl, setTpl] = useState(() => space?.email_templates || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTpl(space?.email_templates || {}); }, [space?.id]);
  const setFor = (kind, patch) => setTpl((s) => ({ ...s, [kind]: { ...(s[kind] || {}), ...patch } }));
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/workspaces/${ws}/studio/spaces/${space.id}`, { email_templates: tpl });
      toast.success("Email templates saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="sb-space-templates">
      <div className="text-xs text-muted-foreground max-w-2xl">
        Override the default booking email copy for <b>{space.name}</b>.
        Leave a field blank to inherit the platform default.
        Available placeholders: <code>{"{{code}}"}</code>, <code>{"{{customer_name}}"}</code>, <code>{"{{customer_email}}"}</code>, <code>{"{{slot_start}}"}</code>, <code>{"{{slot_end}}"}</code>, <code>{"{{total}}"}</code>, <code>{"{{space_name}}"}</code>.
      </div>
      {kinds.map(({ k, label, hint }) => (
        <div key={k} className="border border-border p-4" data-testid={`sb-space-tpl-${k}`}>
          <div className="text-sm font-bold uppercase tracking-widest">{label}</div>
          <div className="text-[10px] text-muted-foreground mb-3">{hint}</div>
          <div className="space-y-2">
            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={(tpl[k] || {}).subject || ""}
                      onChange={(e) => setFor(k, { subject: e.target.value })}
                      placeholder={`e.g. [MyBrand] Booking {{code}} confirmed`}
                      className="rounded-none h-9 font-mono text-xs"
                      data-testid={`sb-space-tpl-${k}-subject`} />
            </div>
            <div>
              <Label className="text-xs">HTML body</Label>
              <Textarea value={(tpl[k] || {}).html || ""}
                          onChange={(e) => setFor(k, { html: e.target.value })}
                          rows={8}
                          placeholder="<h1>Hi {{customer_name}}</h1><p>Your booking {{code}} at {{space_name}} is confirmed for {{slot_start}}.</p>"
                          className="rounded-none font-mono text-xs"
                          data-testid={`sb-space-tpl-${k}-html`} />
            </div>
          </div>
        </div>
      ))}
      <div className="flex justify-end">
        <Button className="rounded-none" onClick={save} disabled={busy} data-testid="sb-space-tpl-save">
          <Save className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Saving…" : "Save templates"}
        </Button>
      </div>
    </div>
  );
}

// Feb 2026 · Amenities editor — tag pill input. Values render on the
// public widget under each space so customers can compare features
// (WiFi, cyclorama, greenscreen, teleprompter, on-site parking, etc.)
// at a glance before picking a studio.
function AmenitiesEditor({ ws, space, onSaved }) {
  const [tags, setTags] = useState(space.amenities || []);
  const [features, setFeatures] = useState(space.features || "");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTags(space.amenities || []); setFeatures(space.features || ""); }, [space?.id]);
  const addTag = () => {
    const t = draft.trim();
    if (!t) return;
    if (tags.some((x) => x.toLowerCase() === t.toLowerCase())) { toast.info("Already added"); return; }
    setTags([...tags, t]); setDraft("");
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/workspaces/${ws}/studio/spaces/${space.id}`, { amenities: tags, features });
      toast.success("Amenities saved");
      onSaved?.({ ...space, amenities: tags, features });
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const SUGGESTIONS = ["WiFi", "Cyclorama", "Greenscreen", "Teleprompter", "Makeup room",
                          "Parking", "Kitchenette", "Podcast booth", "Blackout curtains", "Air-conditioned",
                          "Load-in access", "Audio isolation", "Lighting kit", "24×7 access"];
  return (
    <div className="border border-border p-4 space-y-3" data-testid="sb-space-amenities">
      <div>
        <Label className="text-xs uppercase tracking-widest">Amenities · shown as chips on the widget</Label>
        <div className="flex flex-wrap gap-1 mt-1">
          {tags.map((t, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 border border-primary/40 bg-primary/5 text-primary text-xs"
                    data-testid={`sb-space-amenity-${i}`}>
              {t}
              <button type="button" onClick={() => setTags(tags.filter((_, j) => j !== i))}
                        className="text-primary/60 hover:text-primary">×</button>
            </span>
          ))}
        </div>
        <div className="flex gap-1 mt-2">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                  placeholder="Type an amenity + Enter"
                  className="rounded-none h-8 text-xs flex-1"
                  data-testid="sb-space-amenity-input" />
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={addTag}>Add</Button>
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {SUGGESTIONS.filter((s) => !tags.some((t) => t.toLowerCase() === s.toLowerCase())).slice(0, 12).map((s) => (
            <button key={s} type="button" onClick={() => setTags([...tags, s])}
                      className="text-[10px] px-1.5 py-0.5 border border-border hover:border-primary hover:text-primary text-muted-foreground">
              + {s}
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest">Long-form features (optional)</Label>
        <Textarea rows={3} value={features} onChange={(e) => setFeatures(e.target.value)}
                    placeholder="e.g. Two-camera live-switch podcast rig with Sony FX3 + LED wall backdrop, capable of 4K/60 output..."
                    className="rounded-none text-xs mt-1" data-testid="sb-space-features" />
      </div>
      <div className="flex justify-end">
        <Button className="rounded-none" onClick={save} disabled={busy} data-testid="sb-space-amenities-save">
          <Save className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Saving…" : "Save amenities"}
        </Button>
      </div>
    </div>
  );
}


